import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { emptyRfqAttachment, fileToRfqAttachment } from '../../utils/rfqEntity';
import { IPD_COLORS } from '../../theme';

export default function RfqAttachmentsPanel({ attachments = [], onChange, disabled = false }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickIndex, setPickIndex] = useState(null);

  function addRow() {
    onChange([...attachments, emptyRfqAttachment()]);
  }

  function removeRow(index) {
    onChange(attachments.filter((_, i) => i !== index));
  }

  async function onFilePick(index, file) {
    if (!file) return;
    setUploading(true);
    try {
      const entry = await fileToRfqAttachment(file);
      onChange(attachments.map((row, i) => (i === index ? entry : row)));
    } catch (e) {
      toast.error(e.message || 'Could not upload file');
    } finally {
      setUploading(false);
      setPickIndex(null);
    }
  }

  return (
    <SectionCard
      title="Attachments"
      actions={!disabled && attachments.length < 5 && (
        <InvButton size="small" startIcon={<AddIcon />} onClick={addRow}>
          Add File
        </InvButton>
      )}
    >
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: attachments.length ? 2 : 0 }}>
        Optional — terms sheet, spec document, or drawing (PDF, DOC, JPG; max 5 MB each).
      </Typography>

      {attachments.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          No attachments added.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {attachments.map((row, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: '10px',
                border: `1px solid ${IPD_COLORS.border}`,
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && pickIndex != null) onFilePick(pickIndex, file);
                  e.target.value = '';
                }}
              />
              <InvButton
                size="small"
                variant="outlined"
                tone="teal"
                startIcon={<UploadFileIcon />}
                loading={uploading && pickIndex === index}
                disabled={disabled}
                onClick={() => {
                  setPickIndex(index);
                  fileRef.current?.click();
                }}
              >
                {row.file_name ? 'Replace' : 'Choose File'}
              </InvButton>
              {row.file_name && (
                <Chip
                  label={row.file_name}
                  size="small"
                  sx={{
                    maxWidth: 280,
                    backgroundColor: IPD_COLORS.selectedBg,
                    color: IPD_COLORS.primary,
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              )}
              {!disabled && (
                <IconButton
                  size="small"
                  aria-label="Remove attachment"
                  onClick={() => removeRow(index)}
                  sx={{ color: IPD_COLORS.error.text, ml: 'auto' }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );
}
